import { eachDayOfInterval, parseISO } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import {
  CardData,
  DayData,
  EventData,
  TripData,
  TripMemberData,
  TripRole,
} from '../../firebase/firebase.types';
import {
  getDaysDocs,
  getEventsDocs,
  getTripDocument,
  getTripMembersDocs,
} from '../../firebase/trips.utils';
import { EAddressComponentsType, getLocalityFromAdrString } from '../../utils/Card';
import { AuthUser } from '../user/user.types';
import { getISODate } from '../utils';
import {
  CardID,
  DateISO,
  EventID,
  Trip,
  TripCard,
  TripDay,
  TripDivider,
  TripEvent,
  TripID,
  TripList,
  TripMember,
} from './trip.types';

export const tripTitleForPlace = (place: string) => {
  if (place) {
    return `Adventure to ${place.split(',')[0]}`;
  }
  return 'Adventure';
};

export const getList = (lists: TripList[], listId: string) =>
  lists.find((list) => list.id === listId);

export const isPlaceAlreadyInList = (
  list: TripList,
  cards: { [key: string]: TripCard },
  googlePlaceId: string
) =>
  Boolean(
    list.cardOrder
      ?.map((cardId) => cards[cardId]?.googlePlaceId)
      .find((cardGooglePlaceId) => cardGooglePlaceId === googlePlaceId)
  );

export const getElementByIdInArray = (array: { id: string }[], id: string) =>
  array.find((item) => item.id === id);

// Ordering read-helpers
export const getOrderedListsForDivider = (trip: Trip, divider: TripDivider): TripList[] =>
  divider.listOrder.reduce<TripList[]>((ordered, id) => {
    const list = trip.lists.find((l) => l.id === id);
    return list ? [...ordered, list] : ordered;
  }, []);

/**
 * getOrderedLists - Every TripList in the trip, in full sidebar display
 * order: top-level lists plus lists nested inside dividers, spliced in at
 * the divider's position. Dividers themselves aren't part of the output —
 * only the lists they contain.
 */
export const getOrderedLists = (trip: Trip): TripList[] =>
  trip.listOrder.reduce<TripList[]>((ordered, id) => {
    const list = trip.lists.find((l) => l.id === id);
    if (list) {
      return [...ordered, list];
    }
    const divider = trip.dividers.find((d) => d.id === id);
    return divider ? [...ordered, ...getOrderedListsForDivider(trip, divider)] : ordered;
  }, []);

/**
 * getOrderedCards - Cards in a list, in cardOrder. Falls back to deriving
 * membership from trip.cardIdToListId (unordered) when cardOrder is missing,
 * so a list never silently renders empty.
 */
export const getOrderedCards = (trip: Trip, list: TripList): TripCard[] => {
  if (!list.cardOrder) {
    return Object.values(trip.cards).filter((card) => trip.cardIdToListId[card.id] === list.id);
  }
  return list.cardOrder.reduce<TripCard[]>((ordered, id) => {
    const card = trip.cards[id];
    return card ? [...ordered, card] : ordered;
  }, []);
};

export const getOrderedEvents = (trip: Trip, day: TripDay): TripEvent[] =>
  (day.eventOrder ?? []).reduce<TripEvent[]>((ordered, id) => {
    const event = trip.events[id];
    return event ? [...ordered, event] : ordered;
  }, []);

/**
 * cardDataToTripCard - Converts a raw Firestore CardData document into the
 * app-state TripCard shape (adds the Firestore doc id, normalizes dateAdded
 * from a Firestore Timestamp to an ISO string).
 *
 * @param id - Firestore document id for the card
 * @param cardData - Raw card document data as read from Firestore
 * @param dateAddedFallback - Value to use for dateAdded when it isn't set yet
 *   (callers differ on this today: '' during full trip load, null right after
 *   creation) — pass explicitly to keep existing call-site behavior.
 */
export const cardDataToTripCard = (
  id: CardID,
  cardData: CardData,
  dateAddedFallback: string | null
): TripCard => {
  const dateAdded = cardData.dateAdded as Timestamp | undefined;
  return {
    id,
    ...cardData,
    dateAdded: dateAdded ? dateAdded.toDate().toISOString() : dateAddedFallback,
  };
};

const tripMemberDataToTripMember = (memberData: TripMemberData): TripMember => ({
  ...memberData,
  joinedAt: memberData.joinedAt.toDate().toISOString(),
});

/**
 * getTripMembers - Reads trips/{tripId}/members. For trips that predate the
 * members subcollection (roles-only, no dual-write yet), falls back to
 * deriving membership from Trip.roles so both legacy and new trips return
 * usable data. Per-member joinedAt doesn't exist pre-migration, so the
 * trip's own createdAt is used as the closest available approximation.
 */
export const getTripMembers = async (tripId: TripID): Promise<TripMember[]> => {
  const membersDocs = await getTripMembersDocs(tripId);
  if (!membersDocs.empty) {
    return membersDocs.docs.map((memberDoc) =>
      tripMemberDataToTripMember(memberDoc.data() as TripMemberData)
    );
  }

  const tripDoc = await getTripDocument(tripId);
  const tripData = tripDoc.data() as TripData | undefined;
  if (!tripData) {
    return [];
  }

  const joinedAt = tripData.createdAt ? tripData.createdAt.toDate().toISOString() : '';
  return Object.entries(tripData.roles ?? {}).map(([userId, role]) => ({
    userId,
    role,
    joinedAt,
    invitedBy: null,
  }));
};

/**
 * canEditTrip - Check if user has edit permissions for a trip
 *
 * ROLE HIERARCHY:
 * - OWNER: Full control (edit, delete, share, manage users)
 * - EDITOR: Can edit trip content (add/edit cards, lists, notes)
 * - VIEWER: Read-only access (can view but not modify)
 *
 * @param trip - Trip object containing roles map
 * @param user - Firebase authenticated user
 * @returns boolean - true if user can edit trip content
 */
export const canEditTrip = (trip?: Trip, user?: AuthUser | null) =>
  trip !== undefined &&
  user !== undefined &&
  user !== null &&
  Object.keys(trip.roles).includes(user.uid) &&
  [TripRole.OWNER, TripRole.EDITOR].includes(trip.roles[user.uid]);

/**
 * isOwner - Check if user is the trip owner
 *
 * Trip owners have full administrative control including:
 * - Deleting the entire trip
 * - Managing user roles and permissions
 * - Changing trip privacy settings (public/private)
 * - All editing capabilities
 *
 * @param trip - Trip object containing roles map
 * @param user - Firebase authenticated user
 * @returns boolean - true if user is trip owner
 */
export const isOwner = (trip?: Trip, user?: AuthUser | null) =>
  trip !== undefined &&
  user !== undefined &&
  user !== null &&
  Object.keys(trip.roles).includes(user.uid) &&
  [TripRole.OWNER].includes(trip.roles[user.uid]);

export const getCardLocalities = (cards: TripCard[]) => {
  const localities = new Set<string>();
  cards.forEach((card) => {
    if (card.addressComponents) {
      let doesLocalityExist = false;
      let hasSublocality = false;

      for (const addressComponent of card.addressComponents) {
        if (addressComponent.types.find((type) => type === EAddressComponentsType.locality)) {
          doesLocalityExist = true;
          localities.add(addressComponent.long_name);
          break;
        }
        if (addressComponent.types.find((type) => type === EAddressComponentsType.sublocality)) {
          hasSublocality = true;
          localities.add(addressComponent.long_name);
        }
      }
      if (!hasSublocality && !doesLocalityExist && card.formattedAddress) {
        const result: string | undefined = getLocalityFromAdrString(
          card.addressComponents,
          card.formattedAddress
        );
        if (!result) {
          return;
        }
        localities.add(result);
      }
    }
  });
  return localities.size > 0 ? Array.from(localities) : undefined;
};

export const getDaysMapSnapshot = async (
  tripId: TripID,
  startDateISO?: DateISO,
  endDateISO?: DateISO
) => {
  const days: { [key: DateISO]: TripDay } = {};
  if (startDateISO && endDateISO) {
    const startDate = parseISO(startDateISO);
    const endDate = parseISO(endDateISO);
    eachDayOfInterval({ start: startDate, end: endDate }).forEach((date) => {
      const dateISO = getISODate(date);
      days[dateISO] = { dateISO };
    });
    const daysDocs = await getDaysDocs(tripId);
    daysDocs.forEach((dayDoc) => {
      const data = dayDoc.data() as DayData;
      days[data.dateISO] = {
        id: dayDoc.id,
        ...data,
      };
    });
  }
  return days;
};

export const getEventsMapSnapshot = async (tripId: TripID) => {
  const eventsDocs = await getEventsDocs(tripId);
  const events: { [key: EventID]: TripEvent } = {};
  eventsDocs.forEach((eventDoc) => {
    events[eventDoc.id] = {
      id: eventDoc.id,
      isConfirmed: true,
      ...(eventDoc.data() as EventData),
    };
  });
  return events;
};
